import { useEffect, useMemo, useRef, useState } from "react";
import {
  Upload,
  Loader2,
  FileText,
  Image as ImageIcon,
  Film,
  Trash2,
  Copy,
  Check,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { listMedia, deleteMedia, uploadFile } from "@/lib/api";

const KINDS = [
  { key: "all", label: "Tutti", icon: null },
  { key: "image", label: "Immagini", icon: ImageIcon },
  { key: "pdf", label: "PDF", icon: FileText },
  { key: "video", label: "Video", icon: Film },
];

const ACCEPT_ALL =
  "image/jpeg,image/png,image/webp,application/pdf,video/mp4,video/webm,video/quicktime";

function formatBytes(bytes) {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(String(iso).replace(" ", "T"));
  if (isNaN(d)) return iso;
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function MediaPanel() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const inputRef = useRef(null);

  const load = () => {
    setLoading(true);
    setError("");
    listMedia()
      .then((r) => {
        setItems(r.items || []);
        setCounts(r.counts || {});
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((i) => i.kind === filter)),
    [items, filter]
  );

  const totalCount = items.length;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploadError("");
    setUploading(true);
    const errors = [];
    for (const file of files) {
      try {
        await uploadFile(file);
      } catch (e) {
        errors.push(`${file.name}: ${e.message || "errore"}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
    if (errors.length > 0) setUploadError(errors.join(" · "));
    load();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteMedia(deleteTarget.id);
      setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      setCounts((prev) => ({
        ...prev,
        [deleteTarget.kind]: Math.max(0, (prev[deleteTarget.kind] || 1) - 1),
      }));
      setDeleteTarget(null);
    } catch (e) {
      setError(e.message);
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async (item) => {
    const absolute = new URL(item.url, window.location.origin).toString();
    try {
      await navigator.clipboard.writeText(absolute);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId((v) => (v === item.id ? null : v)), 1500);
    } catch {
      // clipboard non disponibile: nulla da fare
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-media">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#64748B] mb-2">
            Libreria media
          </div>
          <h2 className="text-4xl md:text-5xl tracking-tight font-semibold text-[#0B1E36]">
            {totalCount} <span className="text-[#64748B] font-light">file</span>
          </h2>
        </div>

        <div>
          <Button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="media-upload-btn"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {uploading ? "Caricamento…" : "Carica file"}
          </Button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT_ALL}
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            className="hidden"
          />
        </div>
      </div>

      {/* Filtri per tipo */}
      <div className="flex flex-wrap gap-2">
        {KINDS.map((k) => {
          const Icon = k.icon;
          const active = filter === k.key;
          const count = k.key === "all" ? totalCount : counts[k.key] || 0;
          return (
            <button
              key={k.key}
              type="button"
              onClick={() => setFilter(k.key)}
              data-testid={`media-filter-${k.key}`}
              className={`px-4 py-2 text-xs uppercase tracking-[0.12em] font-bold rounded-md border transition-colors inline-flex items-center gap-2 ${
                active
                  ? "bg-[#0B1E36] text-white border-[#0B1E36]"
                  : "bg-white text-[#0B1E36] border-[#E2E8F0] hover:border-[#0B1E36]"
              }`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {k.label}
              <span
                className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${
                  active ? "bg-white/20" : "bg-[#F1F5F9] text-[#64748B]"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {uploadError && (
        <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">
          {uploadError}
        </div>
      )}

      {error && (
        <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-7 h-7 animate-spin text-[#007AFF]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg py-16 text-center text-[#64748B]">
          {totalCount === 0
            ? "Nessun file caricato. Usa il pulsante «Carica file» per iniziare."
            : "Nessun file con questo filtro."}
        </div>
      ) : (
        <div
          data-testid="media-grid"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
        >
          {filtered.map((m) => (
            <MediaCard
              key={m.id}
              item={m}
              onDelete={() => setDeleteTarget(m)}
              onCopy={() => copyUrl(m)}
              copied={copiedId === m.id}
            />
          ))}
        </div>
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && !busy && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="media-delete-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo file?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="break-all font-medium">
                {deleteTarget?.orig_name || deleteTarget?.filename}
              </span>
              <br />
              Il file verrà rimosso dal server e dal registro. Se è ancora usato in un
              progetto, quell'immagine non sarà più visibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={busy}
              data-testid="media-delete-confirm-btn"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MediaCard({ item, onDelete, onCopy, copied }) {
  const isImage = item.kind === "image";
  const isVideo = item.kind === "video";
  const displayName = item.orig_name || item.filename;

  return (
    <div
      data-testid={`media-item-${item.id}`}
      className="group bg-white border border-[#E2E8F0] rounded-lg overflow-hidden hover:border-[#0B1E36] transition-colors flex flex-col"
    >
      <div className="relative aspect-square bg-[#F8FAFC] flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img
            src={item.url}
            alt={displayName}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : isVideo ? (
          <>
            <video
              src={item.url}
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
              <Film className="w-10 h-10 text-white" />
            </div>
          </>
        ) : (
          <FileText className="w-14 h-14 text-[#FF3B30]" />
        )}

        <div className="absolute top-2 left-2">
          <span className="px-2 py-1 rounded bg-[#0B1E36]/80 text-white text-[10px] uppercase tracking-[0.15em] font-bold">
            {isImage ? "IMG" : isVideo ? "VIDEO" : "PDF"}
          </span>
        </div>

        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded bg-white/95 hover:bg-white text-[#0B1E36]"
            title="Apri in nuova scheda"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <button
            type="button"
            onClick={onCopy}
            className="p-1.5 rounded bg-white/95 hover:bg-white text-[#0B1E36]"
            title="Copia URL"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-green-600" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 rounded bg-white/95 hover:bg-[#FF3B30] hover:text-white text-[#FF3B30]"
            title="Elimina"
            data-testid={`media-delete-${item.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-1">
        <div className="text-xs text-[#0B1E36] truncate font-medium" title={displayName}>
          {displayName}
        </div>
        <div className="flex items-center justify-between text-[10px] text-[#64748B]">
          <span>{formatBytes(item.size_bytes)}</span>
          <span className="truncate ml-2">{formatDate(item.created_at)}</span>
        </div>
      </div>
    </div>
  );
}
